/**
 * Snapshotting a subtree for undo, before it is gone.
 *
 * A `DeleteObjectCommand` restores what was deleted from data it was handed,
 * so every one of those reads has to happen while the objects are still in the
 * scene — `getParent()` is unreliable the moment the delete runs. Shared
 * because two gestures delete a whole subtree: the Delete key, and "Save as
 * asset" replacing the promoted objects with one reference node.
 */

import type { SceneObject } from 'trace-viewer/scene';
import { DeleteObjectCommand, type DeletedObjectEntry } from 'trace-viewer/editor';

/**
 * Capture `target` and everything under it as an undoable delete.
 *
 * Must be called **before** the delete: the entries carry each object's data
 * and its live parent, and both are read here.
 */
export function buildDeleteCommand(target: SceneObject): DeleteObjectCommand {
    const parentObj: SceneObject | null = target.getParent() ?? null;
    const allToDelete = [target, ...target.getDescendants()];
    const deletedEntries: DeletedObjectEntry[] = allToDelete.map(obj => {
        obj.captureTransform();
        obj.refreshSceneData();
        return {
            object: obj,
            data: obj.getSceneObjectData(),
            parent: obj.getParent() ?? null,
        };
    });
    return new DeleteObjectCommand(target, deletedEntries, parentObj);
}
